import { db } from "./DBHelper";
import VendorServices from "services/VendorServices";
import { STORAGE_KEYS, COLLECTIONS, EVENTS } from "../const";
import { eventsEmitter } from "utils/events";

const VENDORS_CACHE_DURATION_IN_MS = 24 * 60 * 60 * 1000;

const VendorHelper = {
  saveVendors: (list) => db.addAll(COLLECTIONS.VENDORS.name, list),

  getVendorsFromDB: () => db.getAll(COLLECTIONS.VENDORS.name),

  getVendorByLocalIdFromDB: (localId) => db.get(COLLECTIONS.VENDORS.name, Number(localId)),

  getVendorByIdFromDB: async (id) => {
    const vendors = await db.filter(COLLECTIONS.VENDORS.name, { id: Number(id) });
    return vendors[0] || null;
  },

  getVendorByAnyIdFromDB: async (id, localId) => {
    if (id) {
      const vendorById = await VendorHelper.getVendorByIdFromDB(id);
      if (vendorById) return vendorById;
    }
    if (localId) {
      return await VendorHelper.getVendorByLocalIdFromDB(localId);
    }
    return null;
  },

  addVendorToDB: (vendor) => db.add(COLLECTIONS.VENDORS.name, vendor),

  updateVendorInDB: (vendor) => db.update(COLLECTIONS.VENDORS.name, vendor.local_id, vendor),

  deleteVendorFromDB: (localId) => db.delete(COLLECTIONS.VENDORS.name, localId),

  saveOrUpdateVendorOffline: async (vendorData) => {
    const existing = await VendorHelper.getVendorByAnyIdFromDB(vendorData.id, vendorData.local_id);
    if (existing) {
      const updated = { ...vendorData, local_id: existing.local_id };
      await VendorHelper.updateVendorInDB(updated);
      return updated;
    } else {
      const local_id = await VendorHelper.addVendorToDB(vendorData);
      return { ...vendorData, local_id };
    }
  },

  fetchAndUpdateVendorFromBE: async (vendorId) => {
    if (!vendorId || Number(vendorId) <= 0) return null;
    try {
      const vendor = await VendorServices.getVendor(vendorId);
      if (vendor && (vendor.id || vendor.name)) {
        const vendorFromDB = await VendorHelper.saveOrUpdateVendorOffline(vendor);
        eventsEmitter.emit(EVENTS.VENDOR_UPDATED, vendorFromDB);
        return vendorFromDB;
      }
    } catch (err) {
      console.warn(`Failed to fetch and update vendor #${vendorId} from BE:`, err);
    }
    return null;
  },

  loadInitialVendors: async (syncFromBE = false) => {
    const now = Date.now();
    const vendorsLastSyncedAt = Number(localStorage.getItem(STORAGE_KEYS.LAST_VENDOR_FETCH_TIME));

    const cacheExpired = !vendorsLastSyncedAt || now - vendorsLastSyncedAt > VENDORS_CACHE_DURATION_IN_MS;

    if (!cacheExpired && !syncFromBE) {
      const cachedVendors = await VendorHelper.getVendorsFromDB();

      if (cachedVendors.length) {
        return {
          vendors: cachedVendors,
          fromCache: true,
        };
      }
    }

    try {
      const vendors = await VendorServices.getVendors();
      const rawVendors = Array.isArray(vendors) ? vendors : vendors?.results || [];

      if (!Array.isArray(rawVendors)) {
        throw new Error("Invalid format for vendors data");
      }

      await VendorHelper.saveVendors(rawVendors);
      const vendorsFromDB = await VendorHelper.getVendorsFromDB();

      localStorage.setItem(STORAGE_KEYS.LAST_VENDOR_FETCH_TIME, String(now));

      return {
        vendors: vendorsFromDB,
        fromCache: false,
      };
    } catch (error) {
      const cachedVendors = await VendorHelper.getVendorsFromDB();

      if (cachedVendors.length) {
        return {
          vendors: cachedVendors,
          fromCache: true,
        };
      }

      throw new Error("No vendor data available from API or IndexedDB");
    }
  },
  getVendor: (id) => VendorServices.getVendor(id),
  getVendorJournals: (id, params) => VendorServices.getVendorJournals(id, params),
};

export default VendorHelper;
